import { findMatches, normKey, parseLine, parseList, type Id, type ParsedLine } from '@odhaczka/core';
import { db } from '../db/db';
import type { Writer } from '../db/commit';
import { newId } from '../db/commit';
import type { ProductLinkRow } from '../db/types';
import { aiCall, aiName } from './ai';

/** Mapa scaleń produktów: id → id ostatecznego produktu. */
export async function loadMergeMap(): Promise<Map<Id, Id>> {
  const all = await db.products.toArray();
  return buildMergeMap(all);
}

/** Mapa scaleń dowolnej encji z mergedInto (produkty, sklepy, sieci): id → id ostatecznego wiersza. */
export function buildMergeMap(rows: readonly { id: Id; mergedInto?: Id | null }[]): Map<Id, Id> {
  const into = new Map(rows.filter((p) => p.mergedInto).map((p) => [p.id, p.mergedInto!]));
  const out = new Map<Id, Id>();
  for (const id of into.keys()) {
    let cur = id;
    for (let i = 0; i < 20 && into.has(cur); i++) cur = into.get(cur)!;
    out.set(id, cur);
  }
  return out;
}

export const resolveWith = (merge: Map<Id, Id>) => (id: Id) => merge.get(id) ?? id;

export interface DraftCandidate {
  productId: Id;
  name: string;
  score: number;
  /** Kandydat zaproponowany przez AI (inna nazwa tego samego produktu). */
  source?: 'ai';
}

export interface DraftItem {
  line: ParsedLine;
  key: string;
  /** Produkt rozpoznany bez pytania (ten sam klucz albo zapamiętana odpowiedź). */
  exact: Id | null;
  /** Podobne produkty — aplikacja pyta, czy to to samo. */
  candidates: DraftCandidate[];
}

/** Rozbiera wklejony tekst i dopasowuje pozycje do znanych produktów (offline). single = cały tekst to jedna pozycja (edycja). */
export async function analyzeText(text: string, single = false): Promise<DraftItem[]> {
  const lines = single ? [parseLine(text)].filter((l) => l !== null) : parseList(text);
  const products = (await db.products.toArray()).filter((p) => !p.deleted && !p.mergedInto);
  const links = (await db.productLinks.toArray()).filter((l) => !l.deleted);
  const refs = products.map((p) => ({ productId: p.id, name: p.name, normKey: p.normKey }));
  const names = new Map(products.map((p) => [p.id, p.name]));
  const linkRefs = links.map((l: ProductLinkRow) => ({ normKey: l.normKey, productId: l.productId, verdict: l.verdict }));
  return lines.map((line) => {
    const key = normKey(line.name);
    const m = findMatches(key, refs, linkRefs);
    return {
      line,
      key,
      exact: m.exact,
      candidates: m.candidates.map((c) => ({ ...c, name: names.get(c.productId) ?? '?' })),
    };
  });
}

/** Kandydaci od AI po kluczu produktu; null = AI pytane, nic pewnego. */
export type AiCandidates = Record<string, DraftCandidate | null>;

/**
 * Dokłada kandydata od AI na początek kandydatów. Stosowane do podglądu i w finalize — analiza tekstu
 * od nowa nie może go zgubić, bo odpowiedź „to samo” na niego przestałaby być ważna (powstałby duplikat produktu).
 */
export function withAi(items: DraftItem[], ai: AiCandidates): DraftItem[] {
  return items.map((item) => {
    const c = !item.exact && Object.hasOwn(ai, item.key) ? ai[item.key] : null;
    if (!c || item.candidates.some((l) => l.productId === c.productId)) return item;
    return { ...item, candidates: [c, ...item.candidates] };
  });
}

/** Pewność, poniżej której propozycji AI nie pokazujemy. */
const AI_MIN_CONFIDENCE = 0.7;
/** Limit pozycji w jednym zapytaniu /api/ai/match. */
const AI_MATCH_BATCH = 50;

interface AiMatchResponse {
  results?: { key?: unknown; productId?: unknown; confidence?: unknown }[];
}

/**
 * Pyta AI o te same produkty pod inną nazwą dla pozycji bez dokładnego dopasowania.
 * Wynik: kandydat (po scaleniach) albo null dla każdego zapytanego klucza; null całości = AI niedostępne.
 * Odrzuca niepewne, nieznane lub usunięte produkty i te, które już są kandydatami lokalnymi.
 */
export async function aiCandidatesFor(items: DraftItem[], signal: AbortSignal): Promise<AiCandidates | null> {
  const out: AiCandidates = Object.fromEntries(items.filter((i) => i.key && !i.exact).map((i) => [i.key, null]));
  // Klucz i nazwa w granicach serwera (inne zostają bez kandydata, żeby nie odrzucił całego zapytania).
  const byKey = new Map(items.filter((i) => Object.hasOwn(out, i.key) && i.key.length <= 200 && aiName(i.line.name)).map((i) => [i.key, i]));
  const keys = [...byKey.keys()];
  if (keys.length === 0) return out;
  const batches: string[][] = [];
  for (let i = 0; i < keys.length; i += AI_MATCH_BATCH) batches.push(keys.slice(i, i + AI_MATCH_BATCH));
  const responses = await Promise.all(
    batches.map((batch) =>
      aiCall<AiMatchResponse>('/api/ai/match', { items: batch.map((key) => ({ key, name: aiName(byKey.get(key)!.line.name) })) }, signal),
    ),
  );
  if (responses.some((r) => !Array.isArray(r?.results))) return null;

  const products = await db.products.toArray();
  const resolve = resolveWith(buildMergeMap(products));
  const byId = new Map(products.map((p) => [p.id, p]));
  for (const r of responses.flatMap((res) => res!.results!)) {
    if (typeof r.key !== 'string' || typeof r.productId !== 'string' || typeof r.confidence !== 'number') continue;
    const item = byKey.get(r.key);
    if (!item || r.confidence < AI_MIN_CONFIDENCE) continue;
    const id = resolve(r.productId);
    const product = byId.get(id);
    if (!product || product.deleted || product.mergedInto) continue;
    if (item.candidates.some((c) => resolve(c.productId) === id)) continue;
    out[r.key] = { productId: id, name: product.name, score: r.confidence, source: 'ai' };
  }
  return out;
}

/** Decyzja użytkownika dla pozycji z kandydatami. */
export type Resolution = { kind: 'same'; productId: Id } | { kind: 'new' };

/**
 * Ustala produkt dla pozycji w ramach transakcji: istniejący, potwierdzony albo nowy.
 * Odpowiedzi zapisuje w słowniku (product_links), żeby nie pytać drugi raz.
 */
export async function resolveProduct(w: Writer, item: DraftItem, resolution?: Resolution, created?: Map<string, Id>): Promise<Id | null> {
  if (!item.key) return null;
  if (item.exact) return item.exact;
  if (resolution?.kind === 'same') {
    await w.set('productLinks', linkId(item.key, resolution.productId), {
      normKey: item.key,
      productId: resolution.productId,
      verdict: 'same',
    });
    return resolution.productId;
  }
  for (const c of item.candidates) {
    await w.set('productLinks', linkId(item.key, c.productId), { normKey: item.key, productId: c.productId, verdict: 'different' });
  }
  // Ten sam nowy produkt dwa razy na jednej liście → jeden produkt.
  const already = created?.get(item.key);
  if (already) return already;
  const existing = await db.products.where('normKey').equals(item.key).filter((p) => !p.deleted && !p.mergedInto).first();
  if (existing) return existing.id;
  const id = newId();
  await w.set('products', id, { name: item.line.name, normKey: item.key, mergedInto: null });
  created?.set(item.key, id);
  return id;
}

// Deterministyczny identyfikator odpowiedzi: ta sama para (klucz, produkt) z dwóch urządzeń = jeden wiersz.
export const linkId = (key: string, productId: Id) => `link:${productId}:${key}`;
